import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import WeeklyScheduleBar from "@/components/WeeklyScheduleBar";

import SEOHead from "@/components/SEOHead";
import DeferredMount from "@/components/DeferredMount";

const QueryProvider = lazy(() => import("@/providers/QueryProvider"));

// Below-fold / non-LCP: code-split + deferred mount off the critical path
const LatestEpisodesCarousel = lazy(() => import("@/components/LatestEpisodesCarousel"));
const Footer = lazy(() => import("@/components/Footer"));
const TopEpisodesGallery = lazy(() => import("@/components/TopEpisodesGallery"));
const CategoriesCarousel = lazy(() => import("@/components/CategoriesCarousel"));
const StatsMapSection = lazy(() => import("@/components/StatsMapSection"));
const MarqueeSection = lazy(() => import("@/components/MarqueeSection"));
const FeaturesSection = lazy(() => import("@/components/FeaturesSection"));
const MentesBrillantesSection = lazy(() => import("@/components/MentesBrillantesSection"));

const UpcomingEpisodesSection = lazy(() => import("@/components/UpcomingEpisodesSection"));
const SubscribeBanner = lazy(() => import("@/components/SubscribeBanner"));
const NewsletterSection = lazy(() => import("@/components/NewsletterSection"));
const FloatingYouTubeButton = lazy(() => import("@/components/FloatingYouTubeButton"));
const FloatingCallButton = lazy(() => import("@/components/FloatingCallButton"));

const homepageJsonLd = {
  "@context": "https://schema.org",
  "@type": "PodcastSeries",
  name: "NBT Podcast",
  alternateName: "Next Best Thing",
  description: "Podcast en español con conversaciones sin filtros sobre emprendimiento, desarrollo personal, finanzas y liderazgo con los líderes más influyentes de Latinoamérica.",
  url: "https://nbtpodcast.com",
  webFeed: "https://anchor.fm/s/f1261cd0/podcast/rss",
  inLanguage: "es",
  author: {
    "@type": "Person",
    name: "Ricardo Quintero",
  },
  publisher: {
    "@type": "Organization",
    name: "NBT Podcast",
    url: "https://nbtpodcast.com",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-772-777-8677",
      contactType: "customer service",
      availableLanguage: ["Spanish", "English"],
    },
  },
};

const Index = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="NBT Podcast con Ricardo Quintero"
        description="NBT Podcast — Conversaciones sin filtros con emprendedores, creadores y líderes latinos. Escucha en YouTube, Spotify y Apple Podcasts."
        canonical="https://nbtpodcast.com/"
        jsonLd={homepageJsonLd}
      />
      <Header />
      <HeroSection />
      <WeeklyScheduleBar />
      {/* Data sections + react-query load only after delay / near viewport */}
      <DeferredMount rootMargin="900px" idleTimeoutMs={4000} minDelayMs={1200}>
        <Suspense fallback={<div className="min-h-[40vh]" aria-hidden />}>
          <QueryProvider>
            <LatestEpisodesCarousel />
          </QueryProvider>
        </Suspense>
      </DeferredMount>
      <DeferredMount rootMargin="200px" idleTimeoutMs={12000} minDelayMs={4000}>
        <Suspense fallback={null}>
          <QueryProvider>
            <TopEpisodesGallery />
            <CategoriesCarousel />
            <StatsMapSection />
            <MarqueeSection />
            <FeaturesSection />
            <MentesBrillantesSection />
            <UpcomingEpisodesSection />
            <SubscribeBanner />
            <NewsletterSection />
            <Footer />
          </QueryProvider>
        </Suspense>
      </DeferredMount>
      <DeferredMount idleTimeoutMs={15000} minDelayMs={6000}>
        <Suspense fallback={null}>
          <QueryProvider>
            <FloatingYouTubeButton />
            <FloatingCallButton />
          </QueryProvider>
        </Suspense>
      </DeferredMount>
    </div>
  );
};

export default Index;
